import type { HrtOption } from "@/app/directories/shop/[clientSlug]/products/[productId]/components/ProductContext";
import {
  getTranslatedProductDescription,
  getTranslatedProductName,
} from "@/app/directories/shop/helper";
import { formatAsDollars } from "@/app/directories/shop/lib/formatAsDollars";
import { ProductType } from "@/lib/graphql/generated-gql";
import { useInsuranceGatedToggle } from "@frontend/components/InsuranceConnector/insurance-connector-provider";
import { getAllEnabledOrgConfigFlagsByOrgId } from "@frontend/lib/actions/actions";
import { getOrganizationAssetsForOrg } from "@frontend/lib/actions/config-actions";
import { EVENT_CONTEXTS } from "@frontend/lib/analytics";
import { trackEvent } from "@frontend/lib/analytics";
import { EVENT_CATEGORIES } from "@frontend/lib/analytics";
import type {
  GetProductsByCategoryIdQuery,
  Hrt,
} from "@frontend/lib/graphql/generated-gql";
import { useTranslations } from "@frontend/lib/next-intl-typed";
import { useBoundStore } from "@frontend/lib/stores/globalStore";
import { Button as MovingBorderButton } from "@frontend/shared/components/ui/moving-border";
import { cn } from "@frontend/shared/lib/utils";
import { sendGTMEvent } from "@next/third-parties/google";
import { useIsFetching, useQuery } from "@tanstack/react-query";
import { useLocale } from "next-intl";
import Link from "next/link";
import { PriceExplainabilityRenderer } from "../PriceExplainability/PriceExplainabilityRenderer";
import { EligibilityButton } from "../eligibility/EligibilityButton";
import { getPurchaseCtaLayout } from "../eligibility/purchase-cta-layout";
import { PendingPricesMessage } from "../pendingPrices/PendingPricesMessage";
import { PriceToggle } from "../priceToggle/priceToggle";
import { usePriceMap } from "../priceToggle/usePriceMap";

const truncateToSentences = (text: string | null, maxSentences = 3) => {
  if (!text) {
    return "";
  }
  // Split into sentences (handles multiple sentence-ending punctuation)
  const sentences = text.match(/[^.!?]+[.!?]+/g) || [];

  if (sentences.length <= maxSentences) {
    return text;
  }

  // Join first maxSentences sentences and add ellipsis
  return `${sentences.slice(0, maxSentences).join("")}...`;
};

const DiscountTag = ({ discountString }: { discountString: string }) => {
  return (
    <div className="absolute top-4 left-4 z-[100] bg-[#FFFFFFAA] rounded-[20px]">
      <p className="config-p3 !text-xs px-3 py-2">{discountString}</p>
    </div>
  );
};

export const FeaturedCarouselSlide = ({
  product,
  clientSlug,
  orgId,
  isMinimal = false,
  onBuyNowClick,
  primaryActionText,
}: {
  product: GetProductsByCategoryIdQuery["findManyProducts"][number];
  clientSlug: string;
  orgId: number;
  isMinimal?: boolean;
  onBuyNowClick?: () => void;
  primaryActionText?: string;
}) => {
  const t = useTranslations("Shop");
  const translatedPrimaryActionText = primaryActionText ?? t("BookNow");

  return (
    <div className="keen-slider__slide flex lg:flex-row sm:flex-col items-start lg:!min-h-full sm:!min-h-[560px] text-6xl font-medium text-black bg-transparent justify-center lg:gap-16 sm:gap-8 lg:px-24 sm:px-0 sm:py-0 relative">
      <div className="rounded-[20px] h-full flex-1 flex flex-col flex-shrink-0 relative w-full">
        {product.displayMetadata?.discountTag && (
          <DiscountTag discountString={product.displayMetadata?.discountTag} />
        )}
        <Link
          href={`/${clientSlug}/products/${product.id}`}
          className="min-w-full sm:h-[531px] rounded-[20px] bg-cover bg-no-repeat lg:hidden sm:block relative"
          onClick={() => {
            sendGTMEvent({
              event: "featured_product_clicked",
              product_name: product.name,
            });
            trackEvent({
              category: EVENT_CATEGORIES.TRANSACTION,
              context: EVENT_CONTEXTS.STOREFRONT,
              action: "featured_product_clicked",
              metadata: {
                product_name: product.name,
              },
            });
          }}
          style={{
            backgroundImage: `url(${
              product.displayMetadata?.imageUrl ?? null
            })`,
            backgroundPosition: "center -100px",
          }}
          aria-label="Product"
        />

        <Link
          href={`/${clientSlug}/products/${product.id}`}
          className="lg:h-[calc(80vh-228px)] lg:w-full sm:h-[531px] min-w-full object-cover rounded-[20px] lg:block sm:hidden relative"
          onClick={() => {
            sendGTMEvent({
              event: "featured_product_clicked",
              product_name: product.name,
            });
            trackEvent({
              category: EVENT_CATEGORIES.TRANSACTION,
              context: EVENT_CONTEXTS.STOREFRONT,
              action: "featured_product_clicked",
              metadata: {
                product_name: product.name,
              },
            });
          }}
        >
          <img
            src={product.displayMetadata?.imageUrl ?? null}
            alt={"Product"}
            className="lg:h-[calc(80vh-252px)] lg:w-full sm:h-[531px] object-cover min-w-full rounded-[20px] lg:block sm:hidden"
          />
        </Link>

        <FeaturedCarouselSlideContentMobile
          product={product}
          clientSlug={clientSlug}
          isMinimal={isMinimal}
          orgId={orgId}
          onBuyNowClick={onBuyNowClick}
          primaryActionText={translatedPrimaryActionText}
        />
      </div>
      <FeaturedCarouselSlideContent
        product={product}
        clientSlug={clientSlug}
        isMinimal={isMinimal}
        orgId={orgId}
        onBuyNowClick={onBuyNowClick}
        primaryActionText={translatedPrimaryActionText}
      />
    </div>
  );
};

export const FeaturedCarouselSlideContentMobile = ({
  product,
  clientSlug,
  isMinimal,
  orgId,
  onBuyNowClick,
  primaryActionText,
}) => {
  const locale = useLocale();
  const languageCode = locale.replace("-", "_");
  const pricingStatus = useBoundStore.use.pricingStatus();
  const isFetchingPrice = !!useIsFetching({ queryKey: ["updatePrices"] });
  const pricesLoaded = pricingStatus === "SUCCESS" && !isFetchingPrice;
  const isPendingPricesMode = useBoundStore.use.isPendingPricesMode();
  const eligibilityCheckResult = useBoundStore.use.eligibilityCheckResult();

  const { data: configFlags } = useQuery({
    queryKey: ["configFlags", orgId],
    queryFn: () => getAllEnabledOrgConfigFlagsByOrgId(orgId),
    enabled: !!orgId,
  });

  const { data: organizationAssets } = useQuery({
    queryKey: ["organizationAssets", orgId],
    queryFn: () => getOrganizationAssetsForOrg(orgId),
    enabled: !!orgId,
  });

  const primaryColor = organizationAssets?.primaryColor ?? "#000000";

  const {
    isInsuredMode,
    handlePriceModeChange,
    lowestPrice,
    isToggleDisabled,
    lowestComparisonPrice,
    isSelfPayOnly,
  } = usePriceMap((product.productToHrt ?? []) as unknown as HrtOption[]);

  const {
    createGatedHandler,
    isInsuranceConnected,
    insuranceState,
    sendInsurance,
  } = useInsuranceGatedToggle();
  const handlePriceModeChangeWithInsuranceCheck = createGatedHandler(
    handlePriceModeChange,
  );

  const isInsurable = !isSelfPayOnly && !configFlags?.no_insured_procedures;
  // Pending prices mode means insurance was connected but unverified — treat
  // it like a fully-connected insurance row so we hide Connect Insurance and
  // render Book Now in the primary (filled) state.
  const isInsuranceEffectivelyConnected =
    isInsuranceConnected || isPendingPricesMode;
  const {
    showConnectInsurance,
    bookNowWidth,
    buttonHeightClass,
    connectInsuranceOuterHeightClass,
  } = getPurchaseCtaLayout({
    isInsurable,
    isInsuranceConnected: isInsuranceEffectivelyConnected,
    isFetchingPrice,
    eligibilityResult: eligibilityCheckResult.result,
    isEligibilitySystemOn: !!configFlags?.storefront_offboard_consumers,
    layout: "stacked",
  });

  const handleBuyNowClick = (e: React.MouseEvent) => {
    e.preventDefault();
    if (
      (product.product_type === ProductType.PdpAndBuyNow ||
        product.product_type === ProductType.BuyNow) &&
      onBuyNowClick
    ) {
      onBuyNowClick();

      sendGTMEvent({
        event: "featured_product_clicked",
        product_name: product.name,
      });
      trackEvent({
        category: EVENT_CATEGORIES.TRANSACTION,
        context: EVENT_CONTEXTS.STOREFRONT,
        action: "featured_product_clicked",
        metadata: {
          product_name: product.name,
        },
      });
    }
  };

  const t = useTranslations("Shop");
  const tStorefront = useTranslations("StorefrontInsurance");

  return (
    <div
      className={cn(
        "flex items-center lg:hidden sm:block absolute bottom-6 w-full",
      )}
    >
      <div className="relative flex flex-col items-start gap-0 rounded-b-[20px]">
        <div
          className="absolute inset-0 rounded-b-[20px] backdrop-blur-xl"
          style={{
            maskImage: "linear-gradient(to top, black 60%, transparent 100%)",
            WebkitMaskImage:
              "linear-gradient(to top, black 60%, transparent 100%)",
          }}
        />
        <div
          className="absolute inset-0 rounded-b-[20px]"
          style={{
            background:
              "linear-gradient(to top, rgba(0,0,0,0.7) 30%, rgba(0,0,0,0.35) 50%, transparent 100%)",
          }}
        />

        <div className="relative z-10 p-3 flex flex-col gap-1 pl-[14px]">
          <h3 className="config-h3 lg:text-[40px] sm:text-[24px] text-white">
            {getTranslatedProductName(product, languageCode)}
          </h3>
          <p className="font-suisse font-light text-sm leading-[18px] w-full text-white">
            {getTranslatedProductDescription(product, languageCode)}
          </p>
        </div>

        <div className="relative z-10 flex flex-col w-full h-full p-3 mb-[2px] gap-3">
          {isMinimal ? (
            <p className="text-xs font-light text-white mx-4">
              {product.productToHrt.length > 1 && (
                <span>{t("StartingAtCategories")} </span>
              )}
              {product.displayMetadata?.preDiscount && (
                <span className="font-mono line-through mr-1">
                  ${product.displayMetadata?.preDiscount}
                </span>
              )}
              <span className="font-mono">
                {formatAsDollars(lowestPrice, t)}
              </span>

              {!configFlags?.no_insured_procedures && " without insurance"}
              {isPendingPricesMode && (
                <PendingPricesMessage className="inline" />
              )}
            </p>
          ) : (
            <PriceToggle
              checked={isInsuredMode}
              onCheckedChange={handlePriceModeChangeWithInsuranceCheck}
              price={lowestPrice}
              lowestComparisonPrice={lowestComparisonPrice}
              disabled={isToggleDisabled}
              hasMultipleHrts={product.productToHrt.length > 1}
              isSelfPayOnlyClient={!!configFlags?.no_insured_procedures}
              isProductSelfPayOnly={isSelfPayOnly}
              isCategoryProduct={false}
              isCardCarousel={false}
              preDiscountprice={product.displayMetadata?.preDiscount}
              pricesLoaded={pricesLoaded}
              priceToggleColor={undefined}
              isFetchingPrice={isFetchingPrice}
              textColorClass="text-white"
            />
          )}
          <PriceExplainabilityRenderer
            hrt={
              {
                ...product.productToHrt[0].hrt,
                orgId: product.orgId,
              } as Hrt
            }
            textColorClass="text-white/70"
          />
          <div className="flex flex-col gap-2 w-full">
            {showConnectInsurance && (
              <MovingBorderButton
                hoverAnimation
                icon
                iconInline
                borderRadius="9999px"
                duration={8000}
                containerClassName={`w-full ${connectInsuranceOuterHeightClass}`}
                className="bg-black text-white border-black px-4 text-xl font-medium"
                onClick={() => sendInsurance({ type: "OPEN" })}
                aria-label={tStorefront("ConnectInsurance")}
              >
                {tStorefront("ConnectInsurance")}
              </MovingBorderButton>
            )}
            <EligibilityButton
              link={`/${clientSlug}/products/${product.id}`}
              orgId={orgId}
              classNames={`${product.product_type === ProductType.PdpAndBuyNow ? "" : buttonHeightClass} flex items-center justify-center ${
                bookNowWidth === "fit" ? "w-fit" : "w-full"
              }`}
            >
              {product.product_type === ProductType.PdpAndBuyNow ? (
                <div className="flex flex-col gap-2 w-full">
                  <button
                    type="button"
                    onClick={handleBuyNowClick}
                    style={
                      isInsuranceEffectivelyConnected
                        ? { backgroundColor: primaryColor || "#000000" }
                        : undefined
                    }
                    className={`rounded-full ${buttonHeightClass} w-full flex items-center justify-center border transition-all duration-200 ${
                      isInsuranceEffectivelyConnected
                        ? "text-white border-transparent hover:opacity-85"
                        : "text-white border-white hover:bg-white hover:text-black"
                    }`}
                  >
                    <p className="text-xl font-medium text-center">
                      {primaryActionText}
                    </p>
                  </button>
                  <Link
                    href={`/${clientSlug}/products/${product.id}`}
                    className={`rounded-full text-white ${buttonHeightClass} w-full flex items-center justify-center transition-all duration-200 hover:underline`}
                    onClick={() => {
                      sendGTMEvent({
                        event: "featured_product_clicked",
                        product_name: product.name,
                      });
                      trackEvent({
                        category: EVENT_CATEGORIES.TRANSACTION,
                        context: EVENT_CONTEXTS.STOREFRONT,
                        action: "featured_product_clicked",
                        metadata: {
                          product_name: product.name,
                        },
                      });
                    }}
                  >
                    <p className="text-xl font-medium text-center">
                      {t("ViewDetails")}
                    </p>
                  </Link>
                </div>
              ) : product.product_type === ProductType.BuyNow ? (
                <button
                  type="button"
                  onClick={handleBuyNowClick}
                  style={{
                    backgroundColor: isInsuranceEffectivelyConnected
                      ? primaryColor || "#000000"
                      : "transparent",
                  }}
                  className={`rounded-full ${buttonHeightClass} w-full flex items-center justify-center border transition-all duration-200 ${
                    isInsuranceEffectivelyConnected
                      ? "text-white border-transparent hover:opacity-85"
                      : "text-white border-white hover:bg-white hover:text-black"
                  }`}
                >
                  <p className="config-h5 !text-xl text-center">
                    {primaryActionText}
                  </p>
                </button>
              ) : (
                <Link
                  href={`/${clientSlug}/products/${product.id}`}
                  className={`rounded-full ${buttonHeightClass} w-full flex items-center justify-center border transition-all duration-200 ${
                    isInsuranceEffectivelyConnected
                      ? "bg-white text-black border-white hover:opacity-85"
                      : "bg-transparent text-white border-white hover:bg-white hover:text-black"
                  }`}
                  onClick={() => {
                    sendGTMEvent({
                      event: "featured_product_clicked",
                      product_name: product.name,
                    });
                    trackEvent({
                      category: EVENT_CATEGORIES.TRANSACTION,
                      context: EVENT_CONTEXTS.STOREFRONT,
                      action: "featured_product_clicked",
                      metadata: {
                        product_name: product.name,
                      },
                    });
                  }}
                >
                  <p className="config-h5 !text-xl text-center">
                    {primaryActionText}
                  </p>
                </Link>
              )}
            </EligibilityButton>
          </div>
        </div>
      </div>
    </div>
  );
};

export const FeaturedCarouselSlideContent = ({
  product,
  clientSlug,
  isMinimal,
  orgId,
  onBuyNowClick,
  primaryActionText,
}) => {
  const locale = useLocale();
  const languageCode = locale.replace("-", "_");
  const pricingStatus = useBoundStore.use.pricingStatus();
  const isFetchingPrice = !!useIsFetching({ queryKey: ["updatePrices"] });
  const pricesLoaded = pricingStatus === "SUCCESS" && !isFetchingPrice;
  const isPendingPricesMode = useBoundStore.use.isPendingPricesMode();
  const eligibilityCheckResult = useBoundStore.use.eligibilityCheckResult();

  const { data: configFlags } = useQuery({
    queryKey: ["configFlags", orgId],
    queryFn: () => getAllEnabledOrgConfigFlagsByOrgId(orgId),
    enabled: !!orgId,
  });

  const { data: organizationAssets } = useQuery({
    queryKey: ["organizationAssets", orgId],
    queryFn: () => getOrganizationAssetsForOrg(orgId),
    enabled: !!orgId,
  });

  const primaryColor = organizationAssets?.primaryColor ?? "#000000";

  const {
    isInsuredMode,
    handlePriceModeChange,
    lowestPrice,
    isToggleDisabled,
    lowestComparisonPrice,
    isSelfPayOnly,
  } = usePriceMap((product.productToHrt ?? []) as unknown as HrtOption[]);

  const {
    createGatedHandler,
    isInsuranceConnected,
    insuranceState,
    sendInsurance,
  } = useInsuranceGatedToggle();
  const handlePriceModeChangeWithInsuranceCheck = createGatedHandler(
    handlePriceModeChange,
  );

  const isInsurable = !isSelfPayOnly && !configFlags?.no_insured_procedures;
  // Pending prices mode means insurance was connected but unverified — treat
  // it like a fully-connected insurance row so we hide Connect Insurance and
  // render Book Now in the primary (filled) state.
  const isInsuranceEffectivelyConnected =
    isInsuranceConnected || isPendingPricesMode;
  const {
    showConnectInsurance,
    bookNowWidth,
    buttonHeightClass,
    connectInsuranceOuterHeightClass,
  } = getPurchaseCtaLayout({
    isInsurable,
    isInsuranceConnected: isInsuranceEffectivelyConnected,
    isFetchingPrice,
    eligibilityResult: eligibilityCheckResult.result,
    isEligibilitySystemOn: !!configFlags?.storefront_offboard_consumers,
    layout: "stacked",
  });

  const t = useTranslations("Shop");
  const tStorefront = useTranslations("StorefrontInsurance");

  const handleBuyNowClick = (e: React.MouseEvent) => {
    e.preventDefault();
    if (
      (product.product_type === ProductType.PdpAndBuyNow ||
        product.product_type === ProductType.BuyNow) &&
      onBuyNowClick
    ) {
      onBuyNowClick();

      sendGTMEvent({
        event: "featured_product_clicked",
        value: product.name,
      });
      trackEvent({
        category: EVENT_CATEGORIES.TRANSACTION,
        context: EVENT_CONTEXTS.STOREFRONT,
        action: "featured_product_clicked",
        metadata: {
          product_name: product.name,
        },
      });
    }
  };

  return (
    <div className={cn("flex-1  h-full items-center lg:flex sm:hidden")}>
      <div className="flex-1 flex flex-col h-full items-start justify-center lg:gap-5 sm:gap-3">
        <h3 className="config-h3 lg:text-[40px] sm:text-[28px]">
          {getTranslatedProductName(product, languageCode)}
        </h3>
        <p className="font-suisse font-light config-p3 line-clamp-3 lg:w-[90%] sm:w-full">
          {truncateToSentences(
            getTranslatedProductDescription(product, languageCode),
          )}
        </p>

        {isMinimal ? (
          <p className="text-xs font-light">
            {product.productToHrt.length > 1 && (
              <span>{t("StartingAtCategories")} </span>
            )}
            {product.displayMetadata?.preDiscount && (
              <span className="font-mono line-through mr-1">
                ${product.displayMetadata?.preDiscount}
              </span>
            )}
            <span className="font-mono">{formatAsDollars(lowestPrice)}</span>
            {!configFlags?.no_insured_procedures &&
              t("WithoutInsuranceCategories")}
            {isPendingPricesMode && <PendingPricesMessage className="inline" />}
          </p>
        ) : (
          <PriceToggle
            checked={isInsuredMode}
            onCheckedChange={handlePriceModeChangeWithInsuranceCheck}
            price={lowestPrice}
            lowestComparisonPrice={lowestComparisonPrice}
            disabled={isToggleDisabled}
            hasMultipleHrts={product.productToHrt.length > 1}
            isSelfPayOnlyClient={!!configFlags?.no_insured_procedures}
            isProductSelfPayOnly={isSelfPayOnly}
            isCategoryProduct={false}
            isCardCarousel={false}
            preDiscountprice={product.displayMetadata?.preDiscount}
            pricesLoaded={pricesLoaded}
            priceToggleColor={undefined}
            isFetchingPrice={isFetchingPrice}
          />
        )}
        <PriceExplainabilityRenderer
          hrt={
            {
              ...product.productToHrt[0].hrt,
              orgId: product.orgId,
            } as Hrt
          }
        />
        <div className="flex flex-col gap-2 w-full">
          {showConnectInsurance && (
            <MovingBorderButton
              hoverAnimation
              icon
              iconInline
              borderRadius="9999px"
              duration={8000}
              containerClassName={`w-full ${connectInsuranceOuterHeightClass}`}
              className="bg-black text-white border-black px-6 text-xl font-medium whitespace-nowrap"
              onClick={() => sendInsurance({ type: "OPEN" })}
              aria-label={tStorefront("ConnectInsurance")}
            >
              {tStorefront("ConnectInsurance")}
            </MovingBorderButton>
          )}
          <EligibilityButton
            link={`/${clientSlug}/products/${product.id}`}
            orgId={orgId}
            classNames={`${product.product_type === ProductType.PdpAndBuyNow ? "" : buttonHeightClass} flex items-center justify-center ${
              bookNowWidth === "fit" ? "max-w-[400px] w-fit" : "w-full"
            }`}
          >
            {product.product_type === ProductType.PdpAndBuyNow ? (
              <div className="flex flex-col gap-2 w-full">
                <button
                  type="button"
                  onClick={handleBuyNowClick}
                  className={`rounded-full ${buttonHeightClass} flex items-center justify-center border transition-all duration-200 w-full ${
                    isInsuranceEffectivelyConnected
                      ? "text-white border-transparent hover:bg-black hover:text-white"
                      : "border-black/40 text-black hover:bg-black hover:text-white hover:border-black"
                  }`}
                  style={
                    isInsuranceEffectivelyConnected
                      ? { backgroundColor: primaryColor || "#000000" }
                      : undefined
                  }
                >
                  <p className="text-xl font-medium text-center">
                    {primaryActionText}
                  </p>
                </button>
                <Link
                  href={`/${clientSlug}/products/${product.id}`}
                  className={`${buttonHeightClass} flex items-center justify-center w-full`}
                  onClick={() => {
                    sendGTMEvent({
                      event: "featured_product_clicked",
                      value: product.name,
                    });
                    trackEvent({
                      category: EVENT_CATEGORIES.TRANSACTION,
                      context: EVENT_CONTEXTS.STOREFRONT,
                      action: "featured_product_clicked",
                      metadata: {
                        product_name: product.name,
                      },
                    });
                  }}
                >
                  <p
                    className="text-xl font-medium text-center underline hover:underline-offset-2 transition-all duration-300"
                    style={{ color: primaryColor || "#000000" }}
                  >
                    {t("ViewDetails")}
                  </p>
                </Link>
              </div>
            ) : product.product_type === ProductType.BuyNow ? (
              <button
                type="button"
                onClick={handleBuyNowClick}
                className={`rounded-full ${buttonHeightClass} flex items-center justify-center border transition-all duration-200 ${
                  bookNowWidth === "fit" ? "w-[140px]" : "w-full"
                } ${
                  isInsuranceEffectivelyConnected
                    ? "text-white border-transparent hover:opacity-85"
                    : "border-black/40 text-black hover:bg-black hover:text-white hover:border-black"
                }`}
                style={{
                  backgroundColor: isInsuranceEffectivelyConnected
                    ? primaryColor || "#000000"
                    : "transparent",
                }}
              >
                <p className="text-center font-medium text-xl">
                  {primaryActionText}
                </p>
              </button>
            ) : (
              <Link
                href={`/${clientSlug}/products/${product.id}`}
                className={`rounded-full ${buttonHeightClass} flex items-center justify-center border transition-all duration-200 ${
                  bookNowWidth === "fit" ? "w-[140px]" : "w-full"
                } ${
                  isInsuranceEffectivelyConnected
                    ? "bg-black text-white border-black/40 hover:opacity-85"
                    : "bg-transparent text-black border-black/40 hover:bg-black hover:text-white hover:border-black"
                }`}
                onClick={() => {
                  sendGTMEvent({
                    event: "featured_product_clicked",
                    value: product.name,
                  });
                  trackEvent({
                    category: EVENT_CATEGORIES.TRANSACTION,
                    context: EVENT_CONTEXTS.STOREFRONT,
                    action: "featured_product_clicked",
                    metadata: {
                      product_name: product.name,
                    },
                  });
                }}
              >
                <p className="text-center font-medium text-xl">
                  {primaryActionText}
                </p>
              </Link>
            )}
          </EligibilityButton>
        </div>
      </div>
    </div>
  );
};
